import React, { useCallback, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { getCoreRowModel } from '@tanstack/react-table';

// MUI Theme
import { Button, Stack } from '@mui/material';

// Utils
import { makeCommentListColumn } from '../../../utils/columns/makeColumn';

// Hooks
import useConfirmDialog from '../../../hooks/useConfirmDialog';
import { useNotifyMessage } from '../../../hooks/useNotifyMessage';

// Serivces
import { getComments, addComment, deleteComment, updateComment } from '../../../services/comment.service';

// Types
import { Comment, CommentDetails } from '../../../types/Comment';
import { ResourceType } from '../../../types/ResourceType';
import { GetCommentRequest } from '../../../requestModels/getCommentRequest';
import { CONFIRM_DIALOG_CANCEL } from '../../../constants/Constants';
import { AppPermissions } from '../../../constants/Permissions';

import { AuthGuard } from '../../common/AuthGuard';

// Common Components
import { useStandardTable as useTable, Table } from '../../common/table/index';
import withForm from '../../common/withForm';
import CustomModal from '../../common/withModal';

// Components
import commentForm, { CommentInput } from './commentForm';
import useExtensions from '../../common/editor/useExtensions';

const CommentForm = withForm(commentForm);

export default function Comments(props: CommentDetails): JSX.Element {
  const { renderDialog, confirm } = useConfirmDialog(CONFIRM_DIALOG_CANCEL);
  const { notifyMessage } = useNotifyMessage();
  const [isOpen, setIsOpen] = useState(false);

  // Determine resourceType and subjectId values conditionally
  const resourceType = props.resourceType ? props.resourceType : ResourceType.Reader;
  const resourceId = props.subjectId ? props.subjectId : props.reader?.id || '';

  const [paramFilter, setParamFilter] = useState<any>({
    sortBy: [
      {
        createdDateTime: -1,
      },
    ],
    resourceType,
    resourceId,
  } as GetCommentRequest);

  const extensions = useExtensions({});

  const formik = useFormik<CommentInput>({
    initialValues: {
      id: '',
      subjectId: resourceId,
      resourceType,
      comment: '',
      secondarySubjects: props.secondarySubjects,
    },
    validationSchema: () => {
      return Yup.object<CommentInput>({
        comment: Yup.string()
          .required('This field is required')
          .test('not-empty-paragraph', 'Content cannot be empty', (value: any) => {
            return value && value !== '<p></p>';
          }),
      });
    },
    enableReinitialize: true, // Enable reinitialization
    onSubmit: async (values) => {
      const { id, comment } = values;
      const payload: Comment = {
        id,
        subjectId: resourceId,
        resourceType,
        comment,
        secondarySubjects: props.secondarySubjects,
      };

      if (id) {
        await updateComment(payload)
          .then(() => {
            notifyMessage('Comment updated', 'success');
          })
          .catch(() => {
            notifyMessage('Unable to update comment', 'error');
          });

        setIsOpen(false);
        forceReload();
      } else {
        await addComment(payload)
          .then(() => {
            notifyMessage('Comment created', 'success');
            setIsOpen(false);
            forceReload();
          })
          .catch(() => {
            notifyMessage('Unable to create comment', 'error');
          });
      }
    },
  });

  const handleEdit = async (comment: Comment) => {
    formik.setValues({
      id: comment.id,
      subjectId: resourceId,
      resourceType,
      comment: comment.comment,
      secondarySubjects: props.secondarySubjects,
    });
    setIsOpen(true);
  };

  const handleDelete = useCallback(
    async (id: string) => {
      const { accepted } = await confirm({});
      if (!accepted) return;
      await deleteComment(id)
        .then(() => {
          notifyMessage('Comment deleted', 'success');
          forceReload();
        })
        .catch(() => {
          notifyMessage('Unable to delete comment', 'error');
        });
    },
    [confirm]
  );

  const columns = useMemo(() => makeCommentListColumn(handleDelete, handleEdit, extensions), []);
  const table = useTable<any>({
    columns,
    accessKey: props.accessKey,
    fetchAsync: getComments,
    params: paramFilter,
    serverSide: true,
  });

  const forceReload = () => setParamFilter({ ...paramFilter, changedAt: new Date() });

  return (
    <>
      <AuthGuard permission={AppPermissions.Comment.Edit}>
        <Stack direction='row' spacing={1} justifyContent='flex-start'>
          <Button
            onClick={() => {
              formik.resetForm();
              formik.setValues({
                id: '',
                subjectId: resourceId,
                resourceType,
                comment: '',
              });

              setIsOpen(true);
            }}
            variant='contained'
            size='small'
            style={{ margin: 5, marginLeft: 3, textTransform: 'none' }}
          >
            Add Comment
          </Button>
        </Stack>
      </AuthGuard>
      {renderDialog()}

      <CustomModal
        title='Comment'
        isOpen={isOpen}
        isValid={formik.isValid}
        setIsOpen={setIsOpen}
        onConfirm={formik.handleSubmit}
        maxWidth='md' //xs,sm,md,lg,xl
      >
        <CommentForm formRef={formik} />
      </CustomModal>
      <Table
        {...table}
        showSearchBar={false}
        showColumnToggler={false}
        showAction={false}
        showSpinner={false}
        getCoreRowModel={getCoreRowModel()}
        getRowProps={(row: any) => {
          return {
            style: {
              opacity: row.original.isSecondaryReference ? 0.5 : 1,
              backgroundColor: row.original.isSecondaryReference ? '#f0f0f0' : 'inherit',
            },
          };
        }}
      />
    </>
  );
}
